import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Route,
  MapPin,
  Clock,
  PawPrint,
  Users,
  Phone,
  StickyNote,
  CheckCircle2,
  CircleDashed,
  Loader2,
  CreditCard,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ANIMAL_LABELS } from '../lib/animals';
import BookingDetailModal from './BookingDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PAYMENT_BADGE = {
  PAID: { className: 'bg-green-100 text-green-800', label: 'Paid' },
  NOT_PAID: { className: 'bg-yellow-100 text-yellow-800', label: 'Unpaid' },
  REFUNDED: { className: 'bg-gray-100 text-gray-700', label: 'Refunded' },
  PENDING: { className: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
};

/**
 * "Today's Route" provider widget — shows the day's RouteZone(s) + every
 * booking grouped by barn/location with stop ordering, animals, payment status,
 * and notes. Stays compact and field-friendly (no full optimization).
 */
export default function TodayRouteCard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/route-day/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading today's route…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const hasRoute = data.routeDays && data.routeDays.length > 0;
  const noStops = data.totalStops === 0;

  return (
    <>
      <Card data-testid="today-route-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Route className="h-5 w-5 text-primary" /> Today's Route
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {DAY_NAMES[data.dayOfWeek]} •{' '}
                {data.totalStops} stop{data.totalStops === 1 ? '' : 's'}
                {data.totalAnimals ? ` • ${data.totalAnimals} animals` : ''}
              </p>
            </div>
            {hasRoute && (
              <div className="flex flex-wrap gap-1">
                {data.routeDays.map((r) => (
                  <Badge key={r.id} variant="secondary" className="text-xs">
                    {r.routeName || r.zoneName}
                    {r.startTime || r.endTime
                      ? ` • ${r.startTime || '—'}–${r.endTime || '—'}`
                      : ''}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {hasRoute &&
            data.routeDays.some((r) => r.serviceArea) && (
              <p className="text-xs text-muted-foreground mt-1">
                Area: {data.routeDays.map((r) => r.serviceArea).filter(Boolean).join(' • ')}
              </p>
            )}
        </CardHeader>

        <CardContent className="space-y-4">
          {!hasRoute && noStops && (
            <p className="text-sm text-muted-foreground">
              No route day set for {DAY_NAMES[data.dayOfWeek]} and no appointments today.
            </p>
          )}
          {!hasRoute && !noStops && (
            <p className="text-xs text-muted-foreground italic">
              No route day configured — showing today's appointments below.
            </p>
          )}

          {data.stopsByLocation.map((group) => (
            <div key={group.label} className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground/90 border-b pb-1">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                <span data-testid={`group-${group.label}`}>{group.label}</span>
                <span className="text-xs text-muted-foreground font-normal">
                  ({group.stops.length} stop{group.stops.length === 1 ? '' : 's'})
                </span>
              </div>

              {group.stops.map((s) => {
                const dt = s.startDateTime ? parseISO(s.startDateTime) : null;
                const paid = s.depositStatus || (s.paymentStatus || 'NOT_PAID');
                const pb = PAYMENT_BADGE[paid] || PAYMENT_BADGE.NOT_PAID;
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setOpenId(s.id)}
                    className="w-full text-left p-3 rounded-lg border hover:border-primary transition-colors"
                    data-testid={`stop-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <StopBadge order={s.stopOrder} status={s.status} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{s.client?.name || 'Client'}</span>
                            {s.client?.phone && (
                              <a
                                href={`tel:${s.client.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-primary inline-flex items-center hover:underline"
                              >
                                <Phone className="h-3 w-3 mr-1" /> {s.client.phone}
                              </a>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {s.service?.name || 'Service'}
                            {dt ? ` • ${format(dt, 'h:mm a')}` : ''}
                          </p>
                          {/* Animals */}
                          {s.animals && s.animals.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                              <PawPrint className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span>
                                {s.animals
                                  .map(
                                    (a) =>
                                      `${a.name}${
                                        a.animalType
                                          ? ` (${ANIMAL_LABELS[a.animalType] || a.animalType})`
                                          : ''
                                      }`
                                  )
                                  .join(', ')}
                              </span>
                            </p>
                          )}
                          {(!s.animals || s.animals.length === 0) &&
                            s.animalQuantity &&
                            s.animalType && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {s.animalQuantity}{' '}
                                {(ANIMAL_LABELS[s.animalType] || s.animalType).toLowerCase()}
                                {s.animalQuantity > 1 ? 's' : ''}
                                {s.groupDescription ? ` — ${s.groupDescription}` : ''}
                              </p>
                            )}
                          {/* Animal alert notes (handling/behavior/medical) */}
                          {s.animals?.some(
                            (a) => a.handlingNotes || a.behaviorNotes || a.medicalNotes
                          ) && (
                            <p className="text-xs text-amber-700 mt-1 italic line-clamp-1">
                              ⚠{' '}
                              {s.animals
                                .map((a) => a.handlingNotes || a.behaviorNotes || a.medicalNotes)
                                .filter(Boolean)
                                .join(' • ')}
                            </p>
                          )}
                          {/* Provider private notes */}
                          {s.providerNotes && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                              <StickyNote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-1">{s.providerNotes}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={pb.className + ' text-xs'}>
                          <CreditCard className="h-3 w-3 mr-1" /> {pb.label}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </CardContent>
      </Card>

      <BookingDetailModal
        bookingId={openId}
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        onUpdated={load}
      />
    </>
  );
}

function StopBadge({ order, status }) {
  const completed = status === 'COMPLETED';
  return (
    <div
      className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        completed
          ? 'bg-green-100 text-green-700'
          : status === 'CONFIRMED'
          ? 'bg-blue-100 text-blue-700'
          : 'bg-yellow-100 text-yellow-800'
      }`}
    >
      {completed ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : order != null ? (
        order
      ) : (
        <CircleDashed className="h-3.5 w-3.5" />
      )}
    </div>
  );
}
